import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import { PostHogProvider as PostHogReactProvider } from 'posthog-js/react';
import { getEnvironmentInfo, getUTMParams, getDeviceInfo } from '@/lib/posthog-helpers';
import { setSuperProperties, trackPageView } from '@/lib/posthog';
import { hasCookie } from '@/lib/cookie-utils';
import {
    initializeSessionReplay,
    evaluateRecordingForPage
} from '@/lib/session-replay-manager';

interface PostHogProviderProps {
    children: React.ReactNode;
}

/**
 * Enhanced PostHog Provider
 * Initializes PostHog with session replay, super properties, and route tracking
 */
export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
    const router = useRouter();

    // Initialize PostHog and configure enhanced features
    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Initialize PostHog
        if (!posthog.__loaded) {
            posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
                api_host: '/ingest',
                ui_host: 'https://us.posthog.com',
                defaults: '2025-05-24',
                capture_pageview: false, // We manually track page views
                capture_pageleave: true,
                debug: process.env.NODE_ENV === 'development',
                opt_out_capturing_persistence_type: 'cookie', // Store opt-out preference in cookies
                disable_session_recording: true, // Start disabled, enable conditionally via manager
                // Session replay configuration
                session_recording: {
                    maskAllInputs: true,
                    maskTextSelector: '[data-private]',
                },
                // Autocapture configuration
                autocapture: {
                    dom_event_allowlist: ['click'], // Only capture clicks
                    url_allowlist: undefined,
                    element_allowlist: ['a', 'button'],
                    css_selector_allowlist: ['[data-track]', '[role="button"]'],
                },
            });
        }

        // Check for team opt-out cookie (production only)
        const isProduction = window.location.hostname === 'www.muzology.com';
        const hasOptOutCookie = hasCookie('muzology_team_opt_out');

        if (isProduction && hasOptOutCookie) {
            posthog.opt_out_capturing();
            console.log('[PostHog] Team member opted out of tracking');
        }

        // Set up super properties (sent with every event)
        const setupSuperProperties = () => {
            const envInfo = getEnvironmentInfo();
            const utmParams = getUTMParams();
            const deviceInfo = getDeviceInfo();

            const superProps: Record<string, any> = {
                ...envInfo,
                ...deviceInfo,
            };

            // Only include UTM params if they exist
            if (Object.keys(utmParams).length > 0) {
                Object.assign(superProps, utmParams);
                // Also track landing page if UTM params are present
                superProps.landing_page = window.location.href;
            }

            // Set all super properties at once
            setSuperProperties(superProps);

            // Store initial landing page if not already stored
            if (!sessionStorage.getItem('initial_landing_page')) {
                sessionStorage.setItem('initial_landing_page', window.location.href);
                setSuperProperties({ initial_landing_page: window.location.href });
            }
        };

        setupSuperProperties();

        // Initialize session replay (environment check happens inside manager)
        if (!hasOptOutCookie) {
            initializeSessionReplay();
        }

        // Capture initial page view
        trackPageView();
    }, []);

    // Track route changes
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            // Update page context super properties
            setSuperProperties({
                page_url: window.location.href,
                page_path: url,
                page_title: document.title,
            });

            // Track page view
            trackPageView(url);

            // Re-evaluate recording for new page (environment check happens inside manager)
            const hasOptOutCookie = hasCookie('muzology_team_opt_out');

            if (!hasOptOutCookie) {
                evaluateRecordingForPage(url);
            }

            console.log('[PostHog] Route change:', url);
        };

        // Subscribe to route changes
        router.events.on('routeChangeComplete', handleRouteChange);

        // Cleanup
        return () => router.events.off('routeChangeComplete', handleRouteChange);
    }, [router.events]);

    return (
        <PostHogReactProvider client={posthog}>
            {children}
        </PostHogReactProvider>
    );
};

export default PostHogProvider;
